import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { InlineLoading } from "@carbon/react";
import { WarningAlt } from "@carbon/icons-react";
import { ScreenAssistantMessage } from "../../types";
import SimpleMarkdown from "../OfferAssistant/SimpleMarkdown";
import ScreenActionCard from "./ScreenActionCard";

export type ActionHandlers = {
  answeringActionId: string | null;
  onConfirm: (actionId: string) => void;
  onCancel: (actionId: string) => void;
};

const AssistantBody = ({
  message,
  handlers,
}: {
  message: ScreenAssistantMessage;
  handlers: ActionHandlers;
}) => {
  const { t } = useTranslation();
  if (message.status === "PENDING") {
    return <InlineLoading description={t("screenAssistant.thinking")} />;
  }
  if (message.status === "FAILED") {
    return (
      <p className="screen-assistant__failed">
        <WarningAlt size={16} aria-hidden="true" />
        {t("screenAssistant.failed")}
      </p>
    );
  }
  return (
    <>
      {message.text && <SimpleMarkdown text={message.text} />}
      {message.actions?.map((action) => (
        <ScreenActionCard
          key={action.id}
          action={action}
          answering={handlers.answeringActionId === action.id}
          onConfirm={handlers.onConfirm}
          onCancel={handlers.onCancel}
        />
      ))}
    </>
  );
};

/** The transcript: the user's turns on the right, the assistant's on the left, newest in view. */
const ScreenAssistantMessages = ({
  messages,
  handlers,
}: {
  messages: ScreenAssistantMessage[];
  handlers: ActionHandlers;
}) => {
  const { t } = useTranslation();
  const listRef = useRef<HTMLOListElement>(null);

  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages]);

  return (
    <ol
      ref={listRef}
      className="screen-assistant__messages"
      aria-label={t("screenAssistant.transcript")}
      aria-live="polite"
    >
      {messages.map((message) => {
        const isUser = message.role === "USER";
        return (
          <li
            key={message.id}
            className={`screen-assistant__bubble screen-assistant__bubble--${
              isUser ? "user" : "assistant"
            }`}
          >
            {isUser ? (
              <p className="screen-assistant__user-text">{message.text}</p>
            ) : (
              <AssistantBody message={message} handlers={handlers} />
            )}
          </li>
        );
      })}
    </ol>
  );
};

export default ScreenAssistantMessages;
